import React from 'react';
import {AnimatePresence, motion} from "framer-motion";
import {useTranslation} from "react-i18next";
import {QRCodeSVG} from "qrcode.react";

// Overlay de célébration : le joueur a gagné le lot distribué à la mi-temps.
// Non bloquant (dismissible) ; le bandeau du leaderboard permet de le rouvrir tant qu'il est valable.
const HalfTimeRewardOverlay = ({isOpen, reward, onClose}) => {
    const {t} = useTranslation();

    if (!reward) return null;

    const rewardLabel = (() => {
        switch (reward.type) {
            case 'DISCOUNT_PERCENT':
                return t('result.rewards.DISCOUNT_PERCENT', {value: reward.value});
            case 'DISCOUNT_AMOUNT':
                return t('result.rewards.DISCOUNT_AMOUNT', {value: reward.value});
            default:
                return reward.title || reward.value;
        }
    })();

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center p-4"
                    initial={{opacity: 0}} animate={{opacity: 1}} exit={{opacity: 0}}
                >
                    <div className="absolute inset-0 bg-slate-900/75 backdrop-blur-sm" onClick={onClose}/>

                    <motion.div
                        initial={{y: 60, opacity: 0, scale: 0.96}}
                        animate={{y: 0, opacity: 1, scale: 1}}
                        exit={{y: 40, opacity: 0}}
                        transition={{type: 'spring', damping: 22, stiffness: 260}}
                        className="relative z-10 w-full max-w-sm bg-brand-dark rounded-3xl p-7 overflow-hidden shadow-2xl"
                    >
                        {/* Halo */}
                        <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-64 h-64 rounded-full bg-indigo-500/25 blur-[70px] pointer-events-none"/>
                        <div className="absolute -bottom-10 -right-10 w-40 h-40 rounded-full bg-yellow-400/10 blur-[60px] pointer-events-none"/>

                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 z-20 w-8 h-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/15 text-gray-400 hover:text-white transition-colors"
                            aria-label={t('common.close', 'Fermer')}
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                            </svg>
                        </button>

                        <div className="relative z-10 text-center">
                            <div className="inline-flex items-center gap-1.5 px-3 py-1 mb-4 rounded-full bg-indigo-500/15 border border-indigo-400/30">
                                <span className="text-sm leading-none">⏱️</span>
                                <span className="text-[10px] font-black text-indigo-300 uppercase tracking-widest">
                                    {t('half_time_reward.overlay_badge', 'Lot mi-temps')}
                                </span>
                            </div>

                            <h3 className="text-2xl font-black text-white uppercase tracking-tight leading-none mb-2">
                                {t('half_time_reward.overlay_title', 'Tu mènes à la pause !')}
                            </h3>
                            <p className="text-sm text-gray-400 font-medium leading-relaxed mb-5">
                                {t('half_time_reward.overlay_subtitle', 'Présente ce QR code au comptoir pour récupérer ton lot.')}
                            </p>

                            {reward.code && (
                                <div className="bg-yellow-50 border-2 border-dashed border-yellow-300 rounded-2xl p-4 mb-4 flex justify-center items-center">
                                    <div className="w-40 h-40 bg-white p-2 rounded-lg shadow-inner">
                                        <QRCodeSVG
                                            value={reward.code}
                                            width="100%"
                                            height="100%"
                                            level="L"
                                            marginSize={0}
                                            fgColor="#000000"
                                            bgColor="#ffffff"
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="text-lg font-black text-white leading-tight mb-1">
                                {rewardLabel}
                            </div>

                            {reward.expiresAt && (
                                <p className="text-[11px] text-gray-500 font-medium mb-5">
                                    {t('result.expires_at', 'Valable jusqu’au')}{' '}
                                    {new Date(reward.expiresAt).toLocaleDateString()}
                                </p>
                            )}

                            <button
                                onClick={onClose}
                                className="w-full py-3.5 bg-white/10 hover:bg-white/15 text-white font-bold text-sm uppercase tracking-widest rounded-xl transition-colors"
                            >
                                {t('half_time_reward.overlay_close', 'Continuer à jouer')}
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default HalfTimeRewardOverlay;
